
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { UserT } from '@/core/types/users.types'
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { users } from '@/core/realisations/users.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

// constants
const TARIFFS = [
    {
        id: 1,
        name: 'Стандартный тариф',
    },
    {
        id: 2,
        name: 'Индивидуальный тариф',
    },
]

export default defineComponent({
    components: {
        modal: Modal,
        'the-select': TheSelect,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel', 'change-standart', 'change-individual'],
    setup(props, { emit }) {
        const state = reactive({
            shipmentTariffId: null as number | null,
            accountantId: null as number | null,
            companyId: null as number | null,
        })

        const state_form$ = useVuelidate(
            {
                shipmentTariffId: { required },
                accountantId: { required },
                companyId: { required: requiredIf(() => state.shipmentTariffId === 2) },
            },
            state
        )

        const data_state = reactive({
            accountants: new DataState<UserT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const emitCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const loadAccountants = async () => {
            try {
                data_state.accountants.is_loading = true

                const response = await users.getAccountants(1)
                data_state.accountants.list = response.data
                data_state.accountants.pagination.total_pages = response.meta.last_page

                data_state.accountants.is_failed = false
                data_state.accountants.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.accountants.failure.catchError(error)

                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_failed = true
                }
            } finally {
                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_loading = false
                }
            }
        }

        const loadMoreAccountants = async (page: number) => {
            if (page > data_state.accountants.pagination.total_pages) return
            data_state.accountants.pagination.current_page = page

            try {
                data_state.accountants.is_loading = true

                const response = await users.getAccountants(page)
                data_state.accountants.list = data_state.accountants.list.concat(response.data)

                data_state.accountants.pagination.total_pages = response.meta.last_page

                data_state.accountants.is_failed = false
                data_state.accountants.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.accountants.failure.catchError(error)

                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_failed = true
                }
            } finally {
                if (!data_state.accountants.failure.error_state.is_cancel) {
                    data_state.accountants.is_loading = false
                }
            }
        }

        const loadCompanies = async (page: number) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    limit: 9999,
                    shipmentTariffId: 2,
                    statusId: 2,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadAccountants()
            loadCompanies(1)
        })

        const onSubmit = () => {
            if (props.loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (state.shipmentTariffId === 1) {
                emit('change-standart', {
                    shipmentTariffId: state.shipmentTariffId,
                    accountantId: state.accountantId,
                })

                return
            }

            if (state.shipmentTariffId === 2) {
                emit('change-individual', {
                    companyId: state.companyId,
                    accountantId: state.accountantId,
                })

                return
            }
        }

        return {
            TARIFFS,
            state,
            state_form$,
            data_state,
            emitCancel,
            loadAccountants,
            loadMoreAccountants,
            loadCompanies,
            onSubmit,
        }
    },
})
